/**
 * MobileSidebar — phone-optimised version of Sidebar.
 *
 * Replaces the desktop Sidebar inside the bottom sheet on screens <= 768px.
 * Designed for one-handed use during a fire emergency, where the user wants
 * the answer to "is there a fire near me?" in one glance, not buried under
 * six nested headers.
 *
 * Layout:
 *   ┌──────────────────────────────────────┐
 *   │ 🔥 N fires · closest 12km            │  ← summary line (peek state)
 *   ├──────────────────────────────────────┤
 *   │ [All] [Emergency] [W&A] [Advice]    │  ← severity filter chips
 *   │  Sort: Distance ▾                    │
 *   ├──────────────────────────────────────┤
 *   │ ◆ Burraga Dam HR              19km   │  ← compact one-row card
 *   │   Planned burn · 19m ago             │
 *   │ ▼ Bingley Way Wamboin        274km   │
 *   │   Advice · 20m ago                   │
 *   │   ⋮                                  │
 *   ├──────────────────────────────────────┤
 *   │ Sign in to set up watch zones →      │  ← bottom CTA (small)
 *   └──────────────────────────────────────┘
 *
 * Design principles:
 *  - Severity-first: Emergency events float to the top regardless of sort.
 *  - One-tap filters: chip row with counts; "Near me" filters by user location.
 *  - Compact cards: 64-72px tall, three or four cards visible without scrolling.
 *  - Watch Zones moved out of the main area (small CTA at bottom or a top
 *    badge once you have zones), not a 300px-tall empty-state panel.
 */

import { useMemo, useState, useCallback, useEffect, useRef, lazy, Suspense } from 'react';
import { UnifiedEvent, formatRelativeTime } from '../types/events';
import { useAuth } from '../contexts/AuthContext';
import type { WatchZone } from '../api/client';
import type { ZoneShape } from './ZoneDrawingLayer';
import { severityColor } from '../utils/severity';
import { SkeletonFireCardList } from './Skeleton';
import { EmptyState } from './EmptyState';
import { FlameIcon, AlertTriangleIcon } from './icons';
import OfficialLinks from './OfficialLinks';
import WatchZonePromotion from './WatchZonePromotion';
import { shouldShowWatchZonePromotion } from '../utils/watchZonePromotion';

// Lazy-loaded — only fetched when the user opens the watch zone manager.
const WatchZoneManager = lazy(() => import('./WatchZoneManager'));

type SeverityFilter = 'ALL' | 'EMERGENCY' | 'WATCH_AND_ACT' | 'ADVICE' | 'OTHER';
type SortOption = 'distance' | 'time' | 'severity';

interface MobileSidebarProps {
  events: UnifiedEvent[];
  isLoading: boolean;
  error: string | null;
  userLocation?: { lat: number; lon: number } | null;
  onEventSelect: (event: UnifiedEvent) => void;
  onLoginClick?: () => void;
  watchZoneCount?: number;
  /** Reserved for the desktop layout's area-context line. Currently unused
   * on mobile — the new compact layout drops the summary section. */
  selectedAreaName?: string;
  // Watch zone manager pass-through (mirrors desktop Sidebar wiring)
  onWatchZoneSelect?: (zone: WatchZone) => void;
  onEditorClose?: () => void;
  onEditorCenterChange?: (center: { lat: number; lon: number }) => void;
  onEditorRadiusChange?: (radiusKm: number) => void;
  onShapeTypeChange?: (shapeType: ZoneShape) => void;
  onDrawingStart?: () => void;
  visibleZoneIds?: Set<string>;
  onToggleZoneVisibility?: (zoneId: string) => void;
  onZoneDeleted?: (zoneId: string) => void;
  /** Official Resources accordion lives in the mobile sheet footer.
   * State is owned by App.tsx so the desktop and mobile versions stay
   * in sync if the viewport flips during use. */
  officialLinksOpen?: boolean;
  onToggleOfficialLinks?: () => void;
  /** Force the sidebar into a specific sub-view. Used by App.tsx to
   * route the user directly into the watch zone manager from the
   * fire-detail "Watch this area" CTA. Null = no override; user
   * controls navigation. */
  requestedView?: 'list' | 'zones' | null;
  /** Pattern #3 — empty-state watch zone CTA. Called when the user
   * taps "Set up Watch Zone" in the empty state. App.tsx is expected
   * to either trigger sign-in (if anon) or navigate to the zones
   * tab with the user's location (or map centre) prefilled. */
  onCreateWatchZoneEmptyState?: () => void;
}

// Severity ordering (lower number = more urgent, shown first)
const SEVERITY_RANK: Record<string, number> = {
  EMERGENCY: 0,
  WATCH_AND_ACT: 1,
  ADVICE: 2,
  NOT_APPLICABLE: 3,
};

function getSeverityRank(event: UnifiedEvent): number {
  const sev = (event.data?.severity as string) || 'NOT_APPLICABLE';
  return SEVERITY_RANK[sev] ?? 4;
}

export default function MobileSidebar({
  events,
  isLoading,
  error,
  userLocation,
  onEventSelect,
  onLoginClick,
  watchZoneCount = 0,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  selectedAreaName: _selectedAreaName,
  onWatchZoneSelect,
  onEditorClose,
  onEditorCenterChange,
  onEditorRadiusChange,
  onShapeTypeChange,
  onDrawingStart,
  visibleZoneIds,
  onToggleZoneVisibility,
  onZoneDeleted,
  officialLinksOpen = false,
  onToggleOfficialLinks,
  requestedView = null,
  onCreateWatchZoneEmptyState,
}: MobileSidebarProps) {
  const { isAuthenticated } = useAuth();
  const [filter, setFilter] = useState<SeverityFilter>('ALL');
  const [sort, setSort] = useState<SortOption>('severity');
  const [nearOnly, setNearOnly] = useState(false);
  // 'list' = fire list (default); 'zones' = watch zone manager
  const [view, setView] = useState<'list' | 'zones'>('list');

  // When App.tsx pushes a requestedView (e.g. from the "Watch this area"
  // CTA in the fire-detail panel) drop the sidebar onto that view. The
  // parent clears the request after a short window so subsequent user
  // navigation isn't stuck on the forced view.
  useEffect(() => {
    if (requestedView && requestedView !== view) {
      setView(requestedView);
    }
    // The dependency on `view` is deliberately omitted — we only want
    // this to fire when the *request* changes, not when the user
    // manually navigates within the sidebar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedView]);
  // Sort popover toggle — the sort control was demoted from a full-width
  // row to a tiny text button next to the filter chips.
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  // Only count fire-relevant events for the headline number — we don't
  // want "54 fires near you" to include power outages and aircraft.
  const fireEvents = useMemo(
    () => events.filter(e => e.type === 'FIRE_INCIDENT' || e.type === 'BURN_AREA'),
    [events]
  );

  // Counts per severity for chip badges.
  const severityCounts = useMemo(() => {
    const counts = { EMERGENCY: 0, WATCH_AND_ACT: 0, ADVICE: 0, OTHER: 0 };
    for (const e of fireEvents) {
      const sev = (e.data?.severity as string) || 'NOT_APPLICABLE';
      if (sev === 'EMERGENCY') counts.EMERGENCY++;
      else if (sev === 'WATCH_AND_ACT') counts.WATCH_AND_ACT++;
      else if (sev === 'ADVICE') counts.ADVICE++;
      else counts.OTHER++;
    }
    return counts;
  }, [fireEvents]);

  // App.tsx already enriches events with _distanceKm when userLocation is
  // available — reuse that rather than recomputing per render.
  const eventsWithDistance = useMemo(
    () => fireEvents.map(e => ({
      event: e,
      distanceKm: typeof e._distanceKm === 'number' ? e._distanceKm : null,
    })),
    [fireEvents]
  );

  // Apply filter + sort.
  const visibleEvents = useMemo(() => {
    let list = eventsWithDistance.slice();

    // Severity filter
    if (filter !== 'ALL') {
      list = list.filter(({ event }) => {
        const sev = (event.data?.severity as string) || 'NOT_APPLICABLE';
        if (filter === 'OTHER') return sev !== 'EMERGENCY' && sev !== 'WATCH_AND_ACT' && sev !== 'ADVICE';
        return sev === filter;
      });
    }

    // "Near me" filter (50 km)
    if (nearOnly && userLocation) {
      list = list.filter(({ distanceKm }) => distanceKm !== null && distanceKm <= 50);
    }

    // Always float emergencies to the top regardless of sort
    list.sort((a, b) => {
      const aRank = getSeverityRank(a.event);
      const bRank = getSeverityRank(b.event);
      if (aRank === 0 && bRank !== 0) return -1;
      if (bRank === 0 && aRank !== 0) return 1;

      if (sort === 'severity') {
        if (aRank !== bRank) return aRank - bRank;
        // tiebreak by distance if available, else time
        if (a.distanceKm !== null && b.distanceKm !== null) return a.distanceKm - b.distanceKm;
      }
      if (sort === 'distance' && a.distanceKm !== null && b.distanceKm !== null) {
        return a.distanceKm - b.distanceKm;
      }
      // time (most recent first)
      const aTime = new Date(a.event.observedAt).getTime();
      const bTime = new Date(b.event.observedAt).getTime();
      return bTime - aTime;
    });

    return list;
  }, [eventsWithDistance, filter, sort, nearOnly, userLocation]);

  const handleEventClick = useCallback(
    (event: UnifiedEvent) => onEventSelect(event),
    [onEventSelect]
  );

  // Watch zone manager view — full-bleed take-over of the sidebar.
  if (view === 'zones') {
    return (
      <div className="mobile-sidebar">
        <div className="mobile-sidebar-zone-header">
          <button
            type="button"
            className="mobile-sidebar-zone-back"
            onClick={() => setView('list')}
            aria-label="Back to fires"
          >
            ←
          </button>
          <h2 className="mobile-sidebar-zone-title">My Watch Zones</h2>
        </div>
        <div className="mobile-sidebar-zone-body">
          <Suspense fallback={null}>
            <WatchZoneManager
              onZoneSelect={onWatchZoneSelect}
              onLoginClick={onLoginClick || (() => {})}
              onEditorClose={onEditorClose}
              onCenterChange={onEditorCenterChange}
              onRadiusChange={onEditorRadiusChange}
              onShapeTypeChange={onShapeTypeChange}
              onDrawingStart={onDrawingStart}
              visibleZoneIds={visibleZoneIds || new Set()}
              onToggleZoneVisibility={onToggleZoneVisibility || (() => {})}
              onZoneDeleted={onZoneDeleted}
            />
          </Suspense>
        </div>
      </div>
    );
  }

  const sortLabel: Record<SortOption, string> = {
    severity: 'Severity',
    distance: 'Distance',
    time: 'Most recent',
  };

  // Pattern #1: first-visit onboarding nudge. Shows ONCE for users who
  // are eligible (no zones yet, not dismissed). Sits above the emergency
  // strip so it's the first thing the user sees when expanding the
  // bottom sheet — high discoverability without permanent UI cost.
  const showOnboardingNudge = onCreateWatchZoneEmptyState && shouldShowWatchZonePromotion({
    isAuthenticated,
    zoneCount: watchZoneCount,
    promotionId: 'watchzone-onboard',
  });

  return (
    <div className="mobile-sidebar">
      {showOnboardingNudge && (
        <WatchZonePromotion
          promotionId="watchzone-onboard"
          variant="banner"
          title="Track fires that matter to you"
          description="Set up a Watch Zone for any area in Australia and we'll alert you when a new fire appears there."
          ctaLabel="Get started"
          onActivate={onCreateWatchZoneEmptyState!}
        />
      )}
      {/* Emergency strip — pinned at the very top when there are emergency
          fires. Tap to filter to just emergencies. The sheet's drag-handle
          already shows the count, but this strip duplicates it inside the
          list because: (a) the bar is hidden once the user scrolls, (b)
          tapping here filters to only emergencies, which the bar can't do. */}
      {severityCounts.EMERGENCY > 0 && filter !== 'EMERGENCY' && (
        <button
          type="button"
          className="mobile-sidebar-emergency-strip"
          onClick={() => setFilter('EMERGENCY')}
          aria-label={`Filter to ${severityCounts.EMERGENCY} emergency fires`}
        >
          <span className="mobile-sidebar-emergency-icon" aria-hidden="true">
            <AlertTriangleIcon size={18} />
          </span>
          <span className="mobile-sidebar-emergency-text">
            <strong>{severityCounts.EMERGENCY} emergency</strong>
            {' — review immediately'}
          </span>
          <span className="mobile-sidebar-emergency-chevron" aria-hidden="true">→</span>
        </button>
      )}

      {/* Filter chips + sort — single horizontally scrollable row. All five
          chips (All / Emergency / W&A / Advice / Near me) live here together
          so the row never wraps. Sort is a tiny button immediately below. */}
      <div
        className="mobile-sidebar-filters mobile-sidebar-filters-scroll"
        role="tablist"
        aria-label="Filter fires"
      >
        <FilterChip
          label="All"
          count={fireEvents.length}
          active={filter === 'ALL'}
          onClick={() => setFilter('ALL')}
        />
        <FilterChip
          label="Emergency"
          count={severityCounts.EMERGENCY}
          active={filter === 'EMERGENCY'}
          onClick={() => setFilter('EMERGENCY')}
          colour={severityColor('EMERGENCY')}
        />
        <FilterChip
          label="Watch & Act"
          count={severityCounts.WATCH_AND_ACT}
          active={filter === 'WATCH_AND_ACT'}
          onClick={() => setFilter('WATCH_AND_ACT')}
          colour={severityColor('WATCH_AND_ACT')}
        />
        <FilterChip
          label="Advice"
          count={severityCounts.ADVICE}
          active={filter === 'ADVICE'}
          onClick={() => setFilter('ADVICE')}
          colour={severityColor('ADVICE')}
        />
        {userLocation && (
          <button
            type="button"
            role="tab"
            aria-selected={nearOnly}
            className={`mobile-sidebar-chip mobile-sidebar-chip-near ${nearOnly ? 'mobile-sidebar-chip-active' : ''}`}
            onClick={() => setNearOnly(v => !v)}
          >
            <span className="mobile-sidebar-chip-icon" aria-hidden="true">📍</span>
            Near me
          </button>
        )}
      </div>

      {/* Sort — tiny text button under the chip row. Opens an inline menu
          rather than claiming a full row for a labeled <select>. */}
      <div className="mobile-sidebar-sortbar">
        <button
          type="button"
          className="mobile-sidebar-sort-button"
          onClick={() => setSortMenuOpen(o => !o)}
          aria-haspopup="menu"
          aria-expanded={sortMenuOpen}
        >
          Sorted by <strong>{sortLabel[sort].toLowerCase()}</strong>
          <span className="mobile-sidebar-sort-caret" aria-hidden="true">{sortMenuOpen ? '▴' : '▾'}</span>
        </button>
        {sortMenuOpen && (
          <div className="mobile-sidebar-sort-menu" role="menu">
            {(['severity', 'distance', 'time'] as SortOption[]).map(option => {
              const disabled = option === 'distance' && !userLocation;
              return (
                <button
                  key={option}
                  type="button"
                  role="menuitemradio"
                  aria-checked={sort === option}
                  disabled={disabled}
                  className={`mobile-sidebar-sort-menu-item ${sort === option ? 'is-active' : ''}`}
                  onClick={() => {
                    if (disabled) return;
                    setSort(option);
                    setSortMenuOpen(false);
                  }}
                >
                  {sortLabel[option]}
                  {disabled && <span className="mobile-sidebar-sort-menu-hint"> (no location)</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Status messages */}
      {error && (
        <div className="mobile-sidebar-status mobile-sidebar-status-error">{error}</div>
      )}

      {/* Compact card list. */}
      <div className="mobile-sidebar-list" role="list" ref={listRef}>
        {isLoading && fireEvents.length === 0 ? (
          <SkeletonFireCardList count={5} />
        ) : visibleEvents.length === 0 ? (
          <>
            <EmptyState
              icon={<FlameIcon size={32} />}
              title={fireEvents.length === 0 ? 'No active fires in view' : 'No fires match your filter'}
              hint={fireEvents.length === 0
                ? 'Pan or zoom the map to see fires elsewhere in Australia.'
                : 'Adjust the severity chips above to see more.'}
              compact
            />
            {/* Pattern #3: watch-zone CTA in the empty state. Only when
                the underlying set is empty (not when a filter happens to
                exclude everything) — the filter case is the user's
                choice, the empty-area case is where the app feels like a
                dead end and the prompt converts that into a useful
                next action. */}
            {fireEvents.length === 0 && onCreateWatchZoneEmptyState && shouldShowWatchZonePromotion({
              isAuthenticated,
              zoneCount: watchZoneCount,
              promotionId: 'watchzone-empty-state',
            }) && (
              <WatchZonePromotion
                promotionId="watchzone-empty-state"
                variant="banner"
                title="Be told when a fire appears here"
                description="No active fires nearby right now. Set up a Watch Zone so we can notify you the moment that changes."
                onActivate={onCreateWatchZoneEmptyState}
              />
            )}
          </>
        ) : (
          visibleEvents.map(({ event, distanceKm: dist }) => (
            <CompactFireCard
              key={event.id}
              event={event}
              distanceKm={dist}
              onClick={handleEventClick}
            />
          ))
        )}
      </div>

      {/* Footer — two-column: Watch Zones CTA on the left, Official
          Resources accordion on the right. Both are tappable surfaces;
          the accordion expands inline above the footer when open. */}
      <div className="mobile-sidebar-footer">
        {officialLinksOpen && onToggleOfficialLinks && (
          <div className="mobile-sidebar-footer-accordion">
            <OfficialLinks isExpanded={true} onToggle={onToggleOfficialLinks} />
          </div>
        )}
        <div className="mobile-sidebar-footer-row">
          {!isAuthenticated ? (
            <button
              type="button"
              className="mobile-sidebar-footer-cta mobile-sidebar-footer-cta-primary"
              onClick={onLoginClick}
            >
              <span className="mobile-sidebar-footer-cta-label">Sign in</span>
              <span className="mobile-sidebar-footer-cta-sub">for watch zone alerts</span>
            </button>
          ) : (
            <button
              type="button"
              className="mobile-sidebar-footer-cta mobile-sidebar-footer-cta-primary"
              onClick={() => setView('zones')}
            >
              <span className="mobile-sidebar-footer-cta-label">
                {watchZoneCount > 0 ? `${watchZoneCount} watch zone${watchZoneCount === 1 ? '' : 's'}` : 'Watch zones'}
              </span>
              <span className="mobile-sidebar-footer-cta-sub">
                {watchZoneCount > 0 ? 'Manage' : 'Set up alerts'}
              </span>
            </button>
          )}
          {onToggleOfficialLinks && (
            <button
              type="button"
              className={`mobile-sidebar-footer-cta mobile-sidebar-footer-cta-secondary ${officialLinksOpen ? 'is-open' : ''}`}
              onClick={onToggleOfficialLinks}
              aria-expanded={officialLinksOpen}
            >
              <span className="mobile-sidebar-footer-cta-label">Official links</span>
              <span className="mobile-sidebar-footer-cta-sub">
                Emergency contacts {officialLinksOpen ? '▾' : '▴'}
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Subcomponents ---------------------------------------------------------

function FilterChip({
  label, count, active, onClick, colour,
}: {
  label: string; count: number; active: boolean; onClick: () => void; colour?: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={`mobile-sidebar-chip ${active ? 'mobile-sidebar-chip-active' : ''}`}
      onClick={onClick}
      style={active && colour ? { borderColor: colour, color: colour } : undefined}
    >
      {label}
      {count > 0 && <span className="mobile-sidebar-chip-count">{count}</span>}
    </button>
  );
}

function CompactFireCard({
  event, distanceKm: dist, onClick,
}: {
  event: UnifiedEvent;
  distanceKm: number | null;
  onClick: (event: UnifiedEvent) => void;
}) {
  const data = (event.data ?? {}) as Record<string, unknown>;
  const severity = (data.severity as string | undefined) ?? 'NOT_APPLICABLE';
  const status = (data.status as string | undefined) ?? '';
  const colour = severityColor(severity);

  // Single-word status for the inline dot+label in the title row. Verbose
  // sentences ("Being controlled") get compressed because every pixel
  // counts on a 360px viewport.
  const statusWord =
    status === 'OUT_OF_CONTROL' ? 'Out of control' :
    status === 'BEING_CONTROLLED' ? 'Controlling' :
    status === 'UNDER_CONTROL' ? 'Controlled' :
    status === 'PATROL' ? 'Patrol' :
    severity === 'EMERGENCY' ? 'Emergency' :
    severity === 'WATCH_AND_ACT' ? 'Watch & act' :
    severity === 'ADVICE' ? 'Advice' :
    severity === 'NOT_APPLICABLE' && (data.fireType as string)?.toLowerCase().includes('hazard')
      ? 'Planned' :
    'Incident';

  return (
    <button
      type="button"
      role="listitem"
      className="mobile-sidebar-card mobile-sidebar-card-dense"
      onClick={() => onClick(event)}
      style={{ borderLeftColor: colour }}
      aria-label={`${event.title}, ${statusWord}${dist !== null ? `, ${Math.round(dist)} km away` : ''}, ${formatRelativeTime(event.observedAt)}`}
    >
      <span className="mobile-sidebar-card-dot" style={{ background: colour }} aria-hidden="true" />
      <span className="mobile-sidebar-card-status-word" style={{ color: colour }}>
        {statusWord}
      </span>
      <span className="mobile-sidebar-card-title">{event.title}</span>
      <span className="mobile-sidebar-card-meta">
        {dist !== null && (
          <span className="mobile-sidebar-card-distance">
            {dist < 1 ? '<1' : Math.round(dist)}km
          </span>
        )}
        <span className="mobile-sidebar-card-time">
          {formatRelativeTime(event.observedAt)}
        </span>
      </span>
    </button>
  );
}
